import {
  Button,
  FormGroup,
  Title,
  Divider,
  ActionGroup,
  Flex,
  TextInput,
} from '@patternfly/react-core'
import { ExclamationCircleIcon } from '@patternfly/react-icons'
import { useCallback, useState, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'

import type { PatientMedicationRecord } from '@app/domain/patient-medication-history/patient-medication-history.interface'
import { ActionLink } from '@app/utils/styles/action-link'
import { SmallButton } from '@app/utils/styles/small-button'

const ButtonDiv = styled.div`
  text-align: center;
`

interface Props {
  activeDate: string
  editRecordDate: (
    activeDate: string,
    medicationRecord: PatientMedicationRecord,
  ) => void
  deleteRecord: (medicationRecordId: string) => void
  medicationRecord: PatientMedicationRecord
}

export const EditRecordPopOverContent = ({
  activeDate,
  editRecordDate,
  deleteRecord,
  medicationRecord,
}: Props) => {
  const [showEditContent, setShowEditContent] = useState(false)
  const [showRemoveWarningContent, setShowRemoveWarningContent] = useState(
    false,
  )
  const [date, setNewDate] = useState(activeDate)

  const handleShowEditContent = useCallback(() => {
    setNewDate(activeDate)
    setShowEditContent(true)
  }, [activeDate])

  const handleCancelEdit = useCallback(() => {
    setNewDate(activeDate)
    setShowEditContent(false)
  }, [activeDate])

  const handleExitFromWarning = useCallback(() => {
    setShowRemoveWarningContent(false)
  }, [])

  const handleShowRemoveWarning = useCallback(() => {
    setShowRemoveWarningContent(true)
  }, [])
  const submitRemoveMedication = useCallback(() => {
    deleteRecord(medicationRecord.id)
  }, [deleteRecord, medicationRecord.id])

  const handleSubmit = useCallback(() => {
    if (new Date(date) instanceof Date && !isNaN(new Date(date).valueOf())) {
      setShowEditContent(false)
      editRecordDate(date, medicationRecord)
      setNewDate(activeDate)
    }
  }, [activeDate, date, editRecordDate, medicationRecord])

  const popOverContent = useMemo(() => {
    if (showEditContent) {
      return (
        <>
          <Title headingLevel="h2">Edit Medication Record</Title>
          <FormGroup label="Date">
            <TextInput type="date" value={date} onChange={setNewDate} />
          </FormGroup>
          <br />
          <ButtonDiv>
            <Button
              style={{ margin: 5 }}
              variant="secondary"
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              Save
            </Button>
          </ButtonDiv>
        </>
      )
    } else if (showRemoveWarningContent) {
      return (
        <>
          <Title headingLevel="h3" style={{ color: 'red' }}>
            <ExclamationCircleIcon /> Danger
          </Title>
          <p style={{ maxWidth: '250px', wordWrap: 'break-word' }}>
            Removing a record will cause each of its associated entries to be
            removed from all medications.
          </p>
          <ActionGroup style={{ marginLeft: 'auto', marginTop: 10 }}>
            <Flex>
              <SmallButton
                style={{ marginRight: 5 }}
                key="cancel"
                variant="secondary"
                onClick={handleExitFromWarning}
              >
                Cancel
              </SmallButton>
              <SmallButton
                key="confirm"
                variant="danger"
                onClick={submitRemoveMedication}
              >
                Continue
              </SmallButton>
            </Flex>
          </ActionGroup>
        </>
      )
    } else {
      return (
        <>
          <div>
            <ActionLink onClick={handleShowEditContent}>Edit</ActionLink>

            <Divider />
            <ActionLink
              style={{ color: 'red' }}
              onClick={handleShowRemoveWarning}
            >
              Remove
            </ActionLink>
          </div>
        </>
      )
    }
  }, [
    date,
    handleCancelEdit,
    handleExitFromWarning,
    handleShowEditContent,
    handleShowRemoveWarning,
    handleSubmit,
    showEditContent,
    showRemoveWarningContent,
    submitRemoveMedication,
  ])

  return popOverContent
}
